import { PanelsTopLeft } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import TablePagination from '../components/TablePagination';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import { usePagination } from '../hooks/usePagination';
import { glassTrackingService } from '../services/api';
import './css/ProductionGlassMonitoring.css';


// Parse "DD.MM.YYYY" → Date (midnight local). Returns null if invalid.
const parseDeliveryDate = (str) => {
  if (!str) return null;
  const [d, m, y] = str.split('.');
  if (!d || !m || !y) return null;
  const date = new Date(Number(y), Number(m) - 1, Number(d));
  return isNaN(date.getTime()) ? null : date;
};

const today = new Date();
today.setHours(0, 0, 0, 0);

const isOverdue = (delivery_date) => {
  const d = parseDeliveryDate(delivery_date);
  return d !== null && d < today;
};

const ProductionGlassMonitoring = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [error, setError] = useState(null);

  // Search / filter
  const [searchTerm, setSearchTerm] = useState('');
  // Filtering runs on the debounced value; clearing the box applies immediately.
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 250);
  const activeSearchTerm = searchTerm.trim() === '' ? '' : debouncedSearchTerm;

  // Producer filter — persisted in URL (?producer=PressGlass)
  const producerFilter = searchParams.get('producer') || 'all';
  const setProducerFilter = (value) => {
    setSearchParams(value === 'all' ? {} : { producer: value });
  };

  // Sorting
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  const fetchIdRef = useRef(0);

  const fetchData = useCallback(async () => {
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    setError(null);
    setData([]);
    try {
      const result = await glassTrackingService.getPendingItems();
      if (fetchId !== fetchIdRef.current) return;
      setData(result || []);
    } catch (err) {
      if (fetchId !== fetchIdRef.current) return;
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      if (fetchId === fetchIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    setError(null);
    try {
      const result = await glassTrackingService.syncEmails();
      setSyncResult(result);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Email sync failed');
    } finally {
      setSyncing(false);
    }
  };

  // ─── Sorting ───
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortIndicator = (field) => {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  // ─── Filtering + Sorting pipeline ───
  const processedData = useMemo(() => {
    let items = [...data];

    if (producerFilter !== 'all') {
      items = items.filter(item => item.producer === producerFilter);
    }

    if (activeSearchTerm.trim()) {
      const term = activeSearchTerm.trim().toLowerCase();
      items = items.filter(item =>
        Object.values(item).some(v => String(v).toLowerCase().includes(term))
      );
    }

    if (sortField) {
      items.sort((a, b) => {
        let va = a[sortField];
        let vb = b[sortField];

        if (!va && !vb) return 0;
        if (!va) return 1;
        if (!vb) return -1;

        // Sort date columns by actual date value, not string
        if (sortField === 'delivery_date') {
          const da = parseDeliveryDate(va);
          const db_ = parseDeliveryDate(vb);
          if (da && db_) return sortDir === 'asc' ? da - db_ : db_ - da;
        }

        if (!isNaN(va) && !isNaN(vb)) {
          return sortDir === 'asc' ? va - vb : vb - va;
        }

        va = String(va ?? '').toLowerCase();
        vb = String(vb ?? '').toLowerCase();
        if (va < vb) return sortDir === 'asc' ? -1 : 1;
        if (va > vb) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return items;
  }, [data, producerFilter, activeSearchTerm, sortField, sortDir]);

  const pagination = usePagination(processedData, {
    storageKey: 'production-glass-monitoring-page-size',
  });

  return (
    <div className="page-container order-monitoring">
      <div className="page-header">
        <h2 className="module-page-title"><PanelsTopLeft aria-hidden="true" /> Monitorowanie szklenia</h2>
        <p>Śledzenie zamówień szyb od PressGlass i AGC Glass.</p>
      </div>
      

      <div className="page-content">
        {/* Toolbar */}
        <div
          className="om-toolbar"
          style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: '0.5rem' }}
        >
          <div className="om-producer-filters">
            {['all', 'PressGlass', 'AGC Glass'].map((p) => (
              <button
                key={p}
                onClick={() => setProducerFilter(p)}
                className={`om-producer-btn${producerFilter === p ? ' om-producer-btn--active' : ''}`}
              >
                {p === 'all' ? 'Full' : p}
              </button>
            ))}
          </div>
        </div>

        {/* Error banner */}
        {error && (
          <div className="om-banner om-banner--error">⚠️ {error}</div>
        )}

        {/* Search bar */}
        <div className="om-search-row">
          <input
            type="text"
            className="om-search"
            placeholder="Szukaj po wszystkich kolumnach…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Data table */}
        <div className="om-table-wrap">
          {loading ? (
            <div className="om-loading">Ładowanie danych…</div>
          ) : processedData.length === 0 ? (
            <div className="om-empty">Brak oczekujących zamówień szklenia.</div>
          ) : (
            <>
              <table className="om-table">
                <thead>
                  <tr>
                    <th
                      onClick={() => handleSort('producer')}
                      style={{ cursor: 'pointer' }}
                    >
                      Producent{sortIndicator('producer')}
                    </th>
                    <th
                      onClick={() => handleSort('order_number')}
                      style={{ cursor: 'pointer' }}
                    >
                      Nr zlecenia{sortIndicator('order_number')}
                    </th>
                    <th
                      onClick={() => handleSort('order_by_customer')}
                      style={{ cursor: 'pointer' }}
                    >
                      Nr zamówienia wg nabywcy{sortIndicator('order_by_customer')}
                    </th>
                    <th
                      onClick={() => handleSort('d_ref')}
                      style={{ cursor: 'pointer' }}
                    >
                      D/Ref{sortIndicator('d_ref')}
                    </th>
                    <th
                      onClick={() => handleSort('dimensions')}
                      style={{ cursor: 'pointer' }}
                    >
                      Wymiar{sortIndicator('dimensions')}
                    </th>
                    <th
                      onClick={() => handleSort('ordered')}
                      style={{ cursor: 'pointer' }}
                    >
                      Zamówiono{sortIndicator('ordered')}
                    </th>
                    <th
                      onClick={() => handleSort('delivered')}
                      style={{ cursor: 'pointer' }}
                    >
                      Dostarczono{sortIndicator('delivered')}
                    </th>
                    <th
                      onClick={() => handleSort('delivery_date')}
                      style={{ cursor: 'pointer' }}
                    >
                      Data dostawy{sortIndicator('delivery_date')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pagination.pageItems.map((item) => (
                    <tr
                      key={`${item.producer}|${item.order_number}|${item.dimensions}|${item.delivery_date}`}
                      className={[
                        item.has_duplicate ? 'om-row--duplicate' : '',
                        isOverdue(item.delivery_date) ? 'om-row--overdue' : '',
                      ].filter(Boolean).join(' ')}
                      title={item.has_duplicate
                        ? `Duplikat: ${item.duplicate_source_filename || 'plik PDF'} — ${item.duplicate_email_subject || item.duplicate_email_uid}`
                        : undefined}
                    >
                      <td>{item.producer}</td>
                      <td>
                        <strong>{item.order_number}</strong>
                        {item.has_duplicate && (
                          <span className="om-duplicate-badge">Duplikat</span>
                        )}
                      </td>
                      <td>{item.order_by_customer || '—'}</td>
                      <td>{item.d_ref || '—'}</td>
                      <td>{item.dimensions}</td>
                      <td>{item.ordered}</td>
                      <td>{item.delivered}</td>
                      <td>{item.delivery_date || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <TablePagination pagination={pagination} unfilteredTotal={data.length} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductionGlassMonitoring;
